import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, User, Shield, UserCheck, Lock, Eye, EyeOff } from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { UsersAPI, CreateUserData, UpdateUserData } from '../../lib/sdk/users';
import { useAuthStore } from '../../stores/authStore';
import { useFeatureAccessStore } from '../../stores/featureAccessStore';
import { toast } from 'react-toastify';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface User {
  _id: string;
  id?: string; // Mongoose sometimes provides both
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: 'SuperAdmin' | 'Admin' | 'Cashier';
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  features?: Record<string, boolean>;
}

const Users: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50); // Default to 50 per page
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Cashier' as 'SuperAdmin' | 'Admin' | 'Cashier',
    password: '',
    confirmPassword: '',
    features: {} as Record<string, boolean>
  });
  
  // Debounced search term (500ms delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  const { user: currentUser } = useAuthStore();
  const { fetchCurrentUserFeatures } = useFeatureAccessStore();
  
  // React Query hooks
  const { data: usersResponse, isLoading: tableLoading, refetch: refetchUsers } = useUsers({
    page,
    limit,
    search: debouncedSearchTerm || undefined,
    role: roleFilter || undefined,
    isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined
  });
  
  // Get stats from separate queries
  const { data: allUsersResponse } = useUsers({ page: 1, limit: 1 });
  const { data: activeUsersResponse } = useUsers({ page: 1, limit: 1, isActive: true });
  const { data: superAdminsResponse } = useUsers({ page: 1, limit: 1, role: 'SuperAdmin' });
  
  const users = usersResponse?.users || [];
  const pagination = usersResponse?.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 };
  const overallStats = {
    totalUsers: allUsersResponse?.pagination?.total || 0,
    activeUsers: activeUsersResponse?.pagination?.total || 0,
    superAdmins: superAdminsResponse?.pagination?.total || 0,
  };
  
  // Mutations
  const createUserMutation = useCreateUser({
    onSuccess: () => {
      toast.success('User created successfully!');
      setShowForm(false);
      resetForm();
      refetchUsers();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    }
  });
  
  const updateUserMutation = useUpdateUser({
    onSuccess: () => {
      toast.success('User updated successfully!');
      setShowForm(false);
      setEditingUser(null);
      resetForm();
      refetchUsers();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    }
  });
  
  const deleteUserMutation = useDeleteUser({
    onSuccess: () => {
      toast.success('User deleted successfully!');
      refetchUsers();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  });

  // Password reset modal state
  const [showResetModal, setShowResetModal] = useState<boolean>(false);
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);

  // Helper function to get user ID consistently
  const getUserId = (user: User) => user._id || user.id || '';

  // Grantable features for role-based access.
  // NOTE: `manageUsers` is intentionally omitted — user management is restricted to
  // SuperAdmin only and cannot be delegated. Every other feature key is listed here so
  // a SuperAdmin can grant any of them to Admins/Cashiers.
  const availableFeatures = {
    manageDonors: 'Manage Donors',
    viewAccounts: 'View Accounts',
    manageAccounts: 'Manage Accounts',
    accessReports: 'View Reports',
    manageDonationTypes: 'Donation Types',
    manageExpenseTypes: 'Expense Type',
    // Income module
    addIncome: 'Add Income',
    manageIncomes: 'Manage Income',
    manageIncomeDepartments: 'Income Departments',
    manageIncomeTypes: 'Income Types',
    // Separate Add and Manage features
    addDonation: 'Add Donation',
    manageDonations: 'Manage Donation',
    addExpense: 'Add Expense',
    manageExpenses: 'Manage Expense',
    // Separate View and Manage features for administrative entities
    viewRegions: 'View Regions',
    manageRegions: 'Manage Regions',
    viewOffices: 'View Offices',
    manageOffices: 'Manage Offices',
    viewControllers: 'View Controllers',
    manageControllers: 'Manage Controllers',
    manageBackupAutomation: 'Manage Backup Automation',
    manageScheduledReports: 'Manage Scheduled Reports',
    viewAutomationHistory: 'View Automation History'
  };

  // Reset to first page when search term or filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, roleFilter, statusFilter]);
  
  const resetForm = () => {
    setFormData({ firstName: '', lastName: '', email: '', phone: '', role: 'Cashier', password: '', confirmPassword: '', features: {} });
  };
  
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    setSubmitting(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData: UpdateUserData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          features: formData.features
        };
        
        if (formData.password) {
          updateData.password = formData.password;
        }

        updateUserMutation.mutate({ id: getUserId(editingUser), data: updateData });
      } else {
        // Create new user
        const createData: CreateUserData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          role: formData.role,
          features: formData.features
        };

        createUserMutation.mutate(createData);
      }
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error('Failed to save user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      password: '',
      confirmPassword: '',
      features: user.features || {}
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('This will permanently delete the user. Continue?')) {
      return;
    }
    deleteUserMutation.mutate(id);
  };

  const handleToggleFeature = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  // Handle user activation/deactivation
  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    try {
      if (currentStatus) {
        const response = await UsersAPI.deactivate(userId);
        if (response.message) {
          toast.success('User deactivated successfully');
          refetchUsers();
        }
      } else {
        const response = await UsersAPI.activate(userId);
        if (response.message) {
          toast.success('User activated successfully');
          refetchUsers();
        }
      }
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status. Try again later.');
    }
  };

  // Handle feature management
  const handleFeatureToggle = async (userId: string, feature: string, currentValue: boolean) => {
    try {
      const currentFeatures = users.find(u => u._id === userId)?.features || {};
      const updatedFeatures = {
        ...currentFeatures,
        [feature]: !currentValue
      };
      
      const response = await UsersAPI.updateFeatures(userId, updatedFeatures);
      
      if (response.message) {
        toast.success('User features updated successfully');
        refetchUsers();
        // If updating own features, refresh current features for navigation
        try {
          if (userId === currentUser?.userId) {
            await fetchCurrentUserFeatures();
          }
        } catch {}
      } else {
        toast.error('Failed to update user features. Please try again.');
      }
    } catch (error) {
      console.error('Failed to update user features:', error);
      toast.error('Failed to update user features. Please try again.');
    }
  };

  // Open password reset modal
  const handlePasswordReset = (userId: string) => {
    setResetUserId(userId);
    setResetPassword('');
    setResetConfirm('');
    setShowResetModal(true);
  };

  // Submit password reset
  const submitPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetUserId) return;
    if (resetPassword.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      return;
    }
    if (resetPassword !== resetConfirm) {
      toast.error('Passwords do not match.');
      return;
    }
    try {
      setResetSubmitting(true);
      const response = await UsersAPI.resetPassword(resetUserId, resetPassword);
      toast.success('Password reset successfully');
      setShowResetModal(false);
      setResetUserId(null);
      setResetPassword('');
      setResetConfirm('');
      await Promise.all([searchUsers(), loadInitialData()]);
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password. Try again later.');
    } finally {
      setResetSubmitting(false);
    }
  };

  // Handle search and filters
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Reset to page 1 for new search (the debounced effect will handle the actual search)
    setPage(1);
  };

  const handleRoleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRoleFilter(e.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SuperAdmin': return 'badge-danger';
      case 'Admin': return 'badge-warning';
      case 'Cashier': return 'badge-info';
      default: return 'badge-secondary';
    }
  };

  const getStatusColor = (status: boolean) => {
    return status ? 'badge-success' : 'badge-warning';
  };

  // Check if current user has permission to manage users
  if (currentUser?.role !== 'SuperAdmin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Only SuperAdmin users can access Users Management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Users Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage system users and their permissions</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{overallStats.totalUsers}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <UserCheck className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{overallStats.activeUsers}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">SuperAdmins</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{overallStats.superAdmins}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Lock className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Locked Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {users.filter(u => !u.isActive).length}
              </p>
            </div>
          </div>
        </div>
      </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name, email, or phone"
                className="form-input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={handleRoleFilterChange}
                className="form-input"
              >
                <option value="">All Roles</option>
                <option value="SuperAdmin">SuperAdmin</option>
                <option value="Admin">Admin</option>
                <option value="Cashier">Cashier</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="form-input"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <Button variant="outline" size="md" onClick={clearFilters}>
              Clear
            </Button>
          </form>
        </div>

       {/* Users Table */}
       <div className="card">
        <TableRoot>
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Email</TH>
                <TH>Phone</TH>
                                 <TH>Role</TH>
                 <TH>Status</TH>
                 <TH>Features</TH>
                 <TH>Last Login</TH>
                 <TH>Created</TH>
                 <TH>Actions</TH>
              </TR>
            </THead>
            <TBody>
                              {tableLoading ? (
                  <TR>
                    <TD colSpan={9} className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <LoadingSpinner />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Searching users...</p>
                    </TD>
                  </TR>
                ) : users.length === 0 ? (
                  <TR>
                    <TD colSpan={9} className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No users found
                    </TD>
                  </TR>
                ) : (
                users.map((user) => (
                  <TR key={user._id}>
                    <TD className="font-medium">{user.firstName} {user.lastName}</TD>
                    <TD className="font-mono">{user.email}</TD>
                    <TD>{user.phone || 'N/A'}</TD>
                    <TD>
                      <span className={`badge ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </TD>
                    <TD>
                      <span className={`badge ${getStatusColor(user.isActive)}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </TD>
                    <TD>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(availableFeatures).map(([key, label]) => (
                          <span
                            key={key}
                            className={`text-xs px-2 py-1 rounded cursor-pointer ${
                              user.features?.[key] 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                            onClick={() => handleFeatureToggle(getUserId(user), key, user.features?.[key] || false)}
                            title={`Click to ${user.features?.[key] ? 'remove' : 'grant'} ${label}`}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </TD>
                    <TD>{user.lastLoginAt || 'N/A'}</TD>
                    <TD>{user.createdAt}</TD>
                    <TD>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(user)}
                          className="flex items-center gap-1"
                        >
                          <Edit className="h-3 w-3" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleStatus(getUserId(user), user.isActive)}
                          className={`flex items-center gap-1 ${
                            user.isActive ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'
                          }`}
                          disabled={user.role === 'SuperAdmin'}
                          title={user.role === 'SuperAdmin' ? 'SuperAdmin users cannot be deactivated' : ''}
                        >
                          {user.isActive ? <Lock className="h-3 w-3" /> : <UserCheck className="h-3 w-3" />}
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePasswordReset(getUserId(user))}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                        >
                          <Shield className="h-3 w-3" />
                          Reset PW
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(getUserId(user))}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          disabled={user.role === 'SuperAdmin'}
                          title={user.role === 'SuperAdmin' ? 'SuperAdmin users cannot be deleted' : 'Delete user permanently'}
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
                     </Table>
         </TableRoot>
         
         {/* Pagination */}
         {pagination.totalPages > 1 && (
           <div className="flex items-center justify-between mt-4">
             <div className="text-sm text-gray-700 dark:text-gray-300">
               Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
             </div>
             
             <div className="flex items-center space-x-2">
               <select
                 value={limit}
                 onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleLimitChange(parseInt(e.target.value))}
                 className="form-input text-sm"
               >
                 <option value={10}>10 per page</option>
                 <option value={20}>20 per page</option>
                 <option value={50}>50 per page</option>
               </select>
               
               <div className="flex space-x-1">
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => handlePageChange(page - 1)}
                   disabled={page === 1 || tableLoading}
                 >
                   Previous
                 </Button>
                 
                 <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                   Page {page} of {pagination.totalPages}
                 </span>
                 
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => handlePageChange(page + 1)}
                   disabled={page === pagination.totalPages || tableLoading}
                 >
                   Next
                 </Button>
               </div>
             </div>
           </div>
         )}
       </div>

      {/* Add/Edit User Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowForm(false)}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  {editingUser ? 'Edit User' : 'Add New User'}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">First Name</label>
                      <Input
                        type="text"
                        value={formData.firstName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, firstName: e.target.value })}
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Last Name</label>
                      <Input
                        type="text"
                        value={formData.lastName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, lastName: e.target.value })}
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Email</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Enter email"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Phone</label>
                      <Input
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Role</label>
                      <select
                        value={formData.role}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, role: e.target.value as any })}
                        className="form-input"
                        required
                        disabled={Boolean(editingUser && getUserId(editingUser) === currentUser?.userId)}
                      >
                        <option value="Cashier">Cashier</option>
                        <option value="Admin">Admin</option>
                        {(!editingUser || getUserId(editingUser) !== currentUser?.userId) && (
                          <option value="SuperAdmin">SuperAdmin</option>
                        )}
                      </select>
                      {editingUser && getUserId(editingUser) === currentUser?.userId && (
                        <p className="text-xs text-gray-500 mt-1">You cannot change your own role</p>
                      )}
                    </div>
                    
                    {!editingUser && (
                      <div>
                        <label className="form-label">Password</label>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, password: e.target.value })}
                            placeholder="Enter password"
                            required={!editingUser}
                          />
                          <span className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {!editingUser && (
                    <div>
                      <label className="form-label">Confirm Password</label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, confirmPassword: e.target.value })}
                          placeholder="Confirm password"
                          required={!editingUser}
                        />
                        <span className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Feature Access Management */}
                  <div>
                    <label className="form-label">Feature Access</label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {Object.entries(availableFeatures).map(([key, value]) => (
                        <label key={key} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.features[key] || false}
                            onChange={() => handleToggleFeature(key)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {value}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} loading={submitting}>
                      {editingUser ? 'Update' : 'Create'} User
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowResetModal(false)}></div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Reset Password</h3>
                <form onSubmit={submitPasswordReset} className="space-y-4">
                  <div>
                    <label className="form-label">New Password</label>
                    <Input
                      type="password"
                      value={resetPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResetPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Confirm Password</label>
                    <Input
                      type="password"
                      value={resetConfirm}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResetConfirm(e.target.value)}
                      placeholder="Confirm new password"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowResetModal(false)}>Cancel</Button>
                    <Button type="submit" disabled={resetSubmitting} loading={resetSubmitting}>Reset</Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;